import { formatTagFilterParam, parseTagFilterParam } from "@/tags/values"

import {
  parseMetadataFilters,
  serializeMetadataFilters,
} from "@/lib/metadataFilterSerialization"
import { serializeToUrlParams } from "@/hooks/filter-params/serialization"
import type { FilterParamSchema } from "@/hooks/filter-params/types"
import type { MetadataFilter } from "@/types/MetadataFilter"

import type { MetricValueFilter } from "../types"
import {
  parseMetricValueFilters,
  serializeMetricValueFilters,
} from "./filterSerialization"

/** URL keys owned by the conversations filter schema, used to detect shared links. */
export const CONVERSATIONS_OWNED_URL_KEYS = [
  "start",
  "end",
  "metrics",
  "agents",
  "tags",
  "projects",
  "metric_filters",
  "metadata",
]

export interface ConversationsUrlState {
  dateRange: { start: Date | null; end: Date | null }
  metricIds: string[]
  agentIds: string[]
  tagFilters: string[]
  projectIds: string[]
  metricValueFilters: MetricValueFilter[]
  metadataFilters: MetadataFilter[]
}

export const CONVERSATIONS_URL_SCHEMA: FilterParamSchema<ConversationsUrlState> =
  {
    dateRange: { type: "dateRange", urlKeys: { start: "start", end: "end" } },
    metricIds: { type: "string[]", urlKey: "metrics" },
    agentIds: { type: "string[]", urlKey: "agents" },
    tagFilters: {
      type: "custom",
      urlKey: "tags",
      serialize: (value) => formatTagFilterParam(value) ?? "",
      parse: parseTagFilterParam,
    },
    projectIds: { type: "string[]", urlKey: "projects" },
    metricValueFilters: {
      type: "custom",
      urlKey: "metric_filters",
      serialize: serializeMetricValueFilters,
      parse: parseMetricValueFilters,
    },
    metadataFilters: {
      type: "custom",
      urlKey: "metadata",
      serialize: serializeMetadataFilters,
      parse: parseMetadataFilters,
    },
  }

export const CONVERSATIONS_URL_DEFAULTS: ConversationsUrlState = {
  dateRange: { start: null, end: null },
  metricIds: [],
  agentIds: [],
  tagFilters: [],
  projectIds: [],
  metricValueFilters: [],
  metadataFilters: [],
}

/**
 * Path to the uploaded conversations list with the given filters applied, in
 * the same URL format the page reads on load.
 */
export function buildUploadedConversationsListPath(
  filters: Partial<ConversationsUrlState>,
): string {
  const params = new URLSearchParams(
    serializeToUrlParams(
      { ...CONVERSATIONS_URL_DEFAULTS, ...filters },
      CONVERSATIONS_URL_SCHEMA,
    ),
  )
  const query = params.toString()

  return query ? `/uploaded?${query}` : "/uploaded"
}
